import { z } from 'zod';
import { lensAnnotationStatesZod } from 'packages/lib/api/annotations/contract/lens-annotation';
import { lidarAnnotationZod } from 'packages/lib/api/annotations/contract/lidar-annotation';
import { annotationObjectZod } from 'packages/lib/api/annotations/contract/annotation-object';

// Mar-09-2026: Used Gemini v2.5 Pro to assist with lidarBoxAnnotationSnapshotItemZod revisions

// Annotation History ("Task Snapshot") backend design doc:
// https://docs.google.com/document/d/1Vwa54IUu139TATsfO1xPkPvQyRmAVj5zeStmx6vqu6U

// Each annotation type needs to be included in the taskSnapshotMetadataZod union

const tldLensAnnotationSnapshotItemZod = z.object({
  isLit: z.boolean(),
  state: lensAnnotationStatesZod,
  lensId: z.number(),
  frameIndex: z.number(),
  taskToken: z.string().optional(),
  videoExtractionToken: z.string(),
  token: z.string(),
  lastUpdatedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const tldSnapshotZod = z.array(tldLensAnnotationSnapshotItemZod);

const evaVideoAnnotationSnapshotItemZod = z.object({
  videoExtractionToken: z.string(),
  instructionToken: z.string(),
  taskToken: z.string().optional(),
  startFrame: z.number(),
  endFrame: z.number(),
  attributeToken: z.string(),
  token: z.string(),
  lastUpdatedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const evaSnapshotZod = z.array(evaVideoAnnotationSnapshotItemZod);
export type EVATaskSnapshot = z.infer<typeof evaSnapshotZod>;

/** LidarBox annotation snapshots are a special case. Other tools must snapshot only one annotation type.
 * LidarBox/RAIL has TWO annotations: Objects (one across all frames per entity) and LidarAnnotations (potentially one per frame, linked to an object).
 * Objects store class such as Person, Animal (does not change across frames). Lidar Annotations store attributes and cuboid geometry (can change across frames)
 * Also, backend snapshot DB does not save certain unnecessary fields (name, tag, etc.) to preserve space, so we must fill those in with proper defaults when missing.
 */
const lidarBoxAnnotationSnapshotItemZod = z.object({
  object: annotationObjectZod.extend({
    // Account for values that are likely missing in the snapshot to save DB space, but required on the frontend
    name: z.string().nullable().default(null),
    tag: z.string().nullable().default(null),
  }),
  lidarAnnotations: z.array(
    lidarAnnotationZod.extend({
      name: z.string().nullable().default(null),
      tag: z.string().nullable().default(null),
      comment: z.string().nullable().default(null),
      className: z.string().nullable().default(null),
    })
  ),
});

export type LidarBoxAnnotationSnapshotItem = z.infer<
  typeof lidarBoxAnnotationSnapshotItemZod
>;
export const lidarBoxSnapshotZod = z.array(lidarBoxAnnotationSnapshotItemZod);
export type LidarBoxTaskSnapshot = z.infer<typeof lidarBoxSnapshotZod>;
export const isLidarboxTaskSnapshot = (obj: unknown): boolean =>
  lidarBoxSnapshotZod.safeParse(obj).success;

const taskSnapshotItemZod = z.union([
  tldLensAnnotationSnapshotItemZod,
  evaVideoAnnotationSnapshotItemZod,
  lidarBoxAnnotationSnapshotItemZod,
]);
export const taskSnapshotZod = z.array(taskSnapshotItemZod);
export type TaskSnapshot = z.infer<typeof taskSnapshotZod>;

export const taskSnapshotMetadataZod = z.object({
  token: z.string(),
  tag: z.string().optional(),
  taskToken: z.string(),
  tableName: z.string(),
  oldAnnotationState: z.string(),
  newAnnotationState: z.string(),
  lastUpdatedBy: z.string(),
  createdAt: z.string(),
});

export const taskSnapshotResponseZod = z.array(taskSnapshotMetadataZod);

export type TaskSnapshotData<T> = z.infer<typeof taskSnapshotMetadataZod> & {
  snapshot: T;
};
export type TaskSnapshotResponse<T> = TaskSnapshotData<T>[];

export const isTaskSnapshot = (obj: unknown): boolean =>
  taskSnapshotMetadataZod.safeParse(obj).success;
